import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { CheckCircle, XCircle, Clock, ArrowRight, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;
    const pollInterval = 2500;

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentDetails(response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (response.data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(pollPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, clearCart]);

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-stone-400 animate-pulse" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
              Processing Your Payment
            </h1>
            <p className="text-stone-500">Please wait while we confirm your order...</p>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-green-600" />
            </motion.div>
            <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1a1a1a] mb-2">
              Order Confirmed!
            </h1>
            <p className="text-stone-500 text-lg mb-8">
              Thank you for your order. We'll start preparing your delicious bakes!
            </p>
            
            {paymentDetails && (
              <div className="bg-stone-50 rounded-2xl p-6 max-w-md mx-auto mb-8">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-stone-200">
                  <span className="text-stone-500">Amount Paid</span>
                  <span className="font-bold text-xl text-[#1a1a1a]">
                    €{(paymentDetails.amount_total / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-left">
                  <Package className="w-5 h-5 text-[#8D6E63]" />
                  <div>
                    <p className="font-medium text-[#1a1a1a]">What's Next?</p>
                    <p className="text-sm text-stone-500">
                      We'll confirm your order details and prepare your items for collection.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop">
                <Button className="bg-[#1a1a1a] hover:bg-[#1a1a1a]/90 rounded-full px-8">
                  Continue Shopping
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="rounded-full px-8">
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        );

      case 'pending':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
              Payment Processing
            </h1>
            <p className="text-stone-500 mb-6">
              Your payment is still being processed. You'll receive a confirmation email shortly.
            </p>
            <Link to="/">
              <Button className="rounded-full">Back to Home</Button>
            </Link>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
              Session Expired
            </h1>
            <p className="text-stone-500 mb-6">
              Your payment session has expired. Please try again.
            </p>
            <Link to="/cart">
              <Button className="rounded-full">Return to Cart</Button>
            </Link>
          </div>
        );

      default:
        return (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1a1a1a] mb-2">
              Something Went Wrong
            </h1>
            <p className="text-stone-500 mb-6">
              We couldn't verify your payment. If you were charged, please contact us.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/cart">
                <Button className="rounded-full">Return to Cart</Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full">Contact Us</Button>
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-4" data-testid="checkout-success-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl w-full py-20"
      >
        {renderContent()}
      </motion.div>
    </div>
  );
}
